import { useCallback, useEffect, useRef, useState } from "react";
import { api, errorText } from "../api.js";
import { Button, Msg, SectionLabel } from "../ui.js";
import { IconBack, IconUndo, IconRedo, IconMusic, IconMic } from "../Icons.js";
import { queue, pendingFor, settle, failed, requestPersistence,
         cacheGet, cacheSet } from "../outbox.js";
import { rememberLease, recallLease, syncNow } from "../sync.js";
import { mediaUrl } from "../media.js";
import * as mic from "../mic.js";
import * as rtc from "../rtc.js";
import { useMatchSocket } from "../useMatchSocket.js";
import { Publisher, available as sfuAvailable } from "../sfu.js";
import * as mix from "../mix.js";
import { Filler } from "../filler.js";
import * as filler from "../filler.js";

import { apply as applyLocal, phaseOf as phaseLocal, RuleViolation,
         type Command, type MatchState, type Ruleset } from "@orbit/rules";
import type { Lang } from "../commentary.js";

import type { Snapshot, Innings, Phase } from "./scoreTypes.js";
import { Scorecard, OverByOver, Feed, buildFeed, type FeedOut, type VoiceClip } from "./Scorecard.js";
import * as voice from "../voice.js";
import { VoiceBar } from "./VoiceBar.js";

type Mods = { wide: boolean; noBall: boolean; byes: boolean; legByes: boolean };
const NO_MODS: Mods = { wide: false, noBall: false, byes: false, legByes: false };

const KINDS = [
  ["BOWLED", "Bowled"], ["CAUGHT", "Caught"], ["LBW", "LBW"], ["RUN_OUT", "Run out"],
  ["STUMPED", "Stumped"], ["HIT_WICKET", "Hit wicket"], ["OBSTRUCTING_FIELD", "Obstructing"],
] as const;
const DEAD_OK = ["RUN_OUT", "OBSTRUCTING_FIELD", "HIT_BALL_TWICE"];
const WIDE_OK = ["RUN_OUT", "STUMPED", "HIT_WICKET", "OBSTRUCTING_FIELD"];

export function Scorer({ matchId, onLeave }: { matchId: string; onLeave: () => void }) {
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [lease, setLease] = useState<string | null>(null);
  const [heldBy, setHeldBy] = useState<string | null>(null);
  const [mods, setMods] = useState<Mods>(NO_MODS);
  const [msg, setMsg] = useState<{ text: string; kind: "err" | "ok" | "warn" } | null>(null);

  const [queued, setQueued] = useState(0);
  const [conn, setConn] = useState<"live" | "polling" | "offline">("polling");
  const [online, setOnline] = useState(navigator.onLine);

  const [wicket, setWicket] = useState<null | { striker: string; nonStriker: string }>(null);
  const [persistWarn, setPersistWarn] = useState(false);
  const [trackSheet, setTrackSheet] = useState(false);
  const [statsTab, setStatsTab] = useState<"play" | "card" | "overs">("play");
  const [lang, setLang] = useState<Lang>("en");
  const [saidAloud, setSaidAloud] = useState<VoiceClip[]>([]);

  // What the scorer has said aloud. Polled with the match rather than pushed:
  // a voice note arriving a few seconds late is no worse than one arriving
  // late because the speaker paused.
  useEffect(() => {
    if (!matchId) return;
    const load = () => void api<{ clips: VoiceClip[] }>(`/api/v1/matches/${matchId}/voice`)
      .then((r) => { if (r.ok) setSaidAloud(r.data.clips); });
    load();
    const t = setInterval(() => { if (navigator.onLine) load(); }, 10000);
    return () => clearInterval(t);
  }, [matchId]);
  const [voiceOn, setVoiceOn] = useState(false);
  const spokenUpTo = useRef<number>(-1);
  const gap = useRef<Filler | null>(null);
  /**
   * Live-audio signalling rides this socket rather than opening another.
   *
   * The talk button registers a handler here; the socket hands it every RTC_*
   * frame. A ref rather than state on purpose — re-rendering the whole scorer
   * because an ICE candidate arrived would be absurd, and there are dozens of
   * them per listener.
   */
  const rtcIn = useRef<((m: Record<string, unknown>) => void) | null>(null);
  const sendSignal = useCallback((m: Record<string, unknown>) => {
    const sock = ws.current;
    if (sock && sock.readyState === WebSocket.OPEN) sock.send(JSON.stringify(m));
  }, []);
  const snapRef = useRef<Snapshot | null>(null);
  snapRef.current = snap;
  /**
   * How far ahead the screen is of anything the server has confirmed.
   *
   * A delivery is applied locally the instant it is tapped. The five-second
   * poll and the socket both write snapshots too, and one landing between the
   * local apply and the server's reply would revert the screen — so the NEXT
   * tap would compute the striker from stale state and the engine would refuse
   * it with BATTER_DESYNC. That is what "the app and the scorer disagree about
   * who is on strike" was: not a real disagreement, a race.
   *
   * Every write goes through accept(), which never moves the screen backwards.
   */
  const localAhead = useRef(0);
  const [tracks, setTracks] = useState<{ id: string; title: string }[]>([]);
  const [playing, setPlaying] = useState<string | null>(null);
  const music = useRef<HTMLAudioElement | null>(null);


  const inn = snap?.state.innings[snap.state.currentInnings] ?? null;
  const doubleWicket =
    (snap?.meta.ruleset as unknown as { dismissals?: { batterContinuesAfterOut?: boolean } })
      ?.dismissals?.batterContinuesAfterOut === true;
  const rs = snap?.meta.ruleset;
  const name = (id: string) => snap?.meta.players[id] ?? "—";

  /* ── loading ──────────────────────────────────────────────────────────── */

  /**
   * Take a snapshot only if it is at least as new as what is on screen.
   *
   * `ledgerSize` counts every event the match has ever had, so it only grows.
   * A snapshot with fewer events than the screen is showing is one that was in
   * flight before the last ball and has arrived late.
   */
  const accept = useCallback((next: Snapshot, from: "server" | "local") => {
    const cur = snapRef.current;
    if (from === "local") {
      localAhead.current += 1;
      setSnap(next);
      return;
    }
    const ahead = (cur?.ledgerSize ?? 0) + localAhead.current;
    if (next.ledgerSize < ahead) return;      // stale; the screen is further on
    localAhead.current = 0;
    setSnap(next);
    void cacheSet(`snap:${matchId}`, next);
  }, [matchId]);

  const load = useCallback(async () => {
    const r = await api<Snapshot>(`/api/v1/matches/${matchId}/snapshot`);
    if (r.ok) accept(r.data, "server");
    else if (!snapRef.current) setMsg({ text: "Could not load the match.", kind: "err" });
  }, [matchId, accept]);

  const countQueued = useCallback(async () => {
    setQueued((await pendingFor(matchId)).length);
  }, [matchId]);

  useEffect(() => {
    // Paint from disk first. A scorer opening the app at a ground on one bar
    // sees the match immediately, then it corrects itself when the server
    // answers — instead of staring at "Loading the match…".
    void cacheGet<Snapshot>(`snap:${matchId}`).then((hit) => {
      if (hit && !snapRef.current) setSnap(hit.value);
    });
    void load();
    // A reload used to lose the lease token, so anything queued could never be
    // sent — it would have been refused with NO_LEASE.
    void recallLease(matchId).then((t) => { if (t) setLease(t); });
    void api<{ held: boolean; displayName?: string }>(`/api/v1/matches/${matchId}/lease`)
      .then((r) => { if (r.ok && r.data.held) setHeldBy(r.data.displayName ?? null); });
    void requestPersistence().then((ok) => setPersistWarn(!ok));
    void countQueued();
    void api<{ clubs: { id: string }[] }>("/api/v1/clubs").then((r) => {
      const cid = r.ok ? r.data.clubs[0]?.id : undefined;
      if (!cid) return;
      void api<{ tracks: { id: string; title: string }[] }>(`/api/v1/clubs/${cid}/audio`)
        .then((t) => { if (t.ok) setTracks(t.data.tracks); });
    });
  }, [matchId, load, countQueued]);

  /* ── the socket, with polling underneath ──────────────────────────────── */

  const ws = useMatchSocket({
    matchId, lease, load,
    onSnapshot: (sn) => accept(sn as Snapshot, "server"),
    onSignal: (m) => rtcIn.current?.(m),
    onLeaseLost: () => {
      setLease(null);
      setMsg({ text: "The scoring pen was taken over.", kind: "warn" });
    },
    onConnection: setConn,
  });

  /* ── sending ──────────────────────────────────────────────────────────── */

  useEffect(() => {
    const mark = () => setOnline(navigator.onLine);
    window.addEventListener("online", mark);
    window.addEventListener("offline", mark);
    return () => {
      window.removeEventListener("online", mark);
      window.removeEventListener("offline", mark);
    };
  }, []);

  const send = useCallback(async (command: unknown) => {
    if (!snapRef.current) return;
    const before = snapRef.current;

    // Apply locally FIRST. The engine is pure and already in this bundle, so
    // the result is exactly what the server will compute — the round trip was
    // only ever confirming what we could work out ourselves. This is the
    // difference between a keypad that responds in 400ms and one that responds
    // before your finger leaves the glass.
    let optimistic: Snapshot | null = null;
    try {
      const next = applyLocal(
        before.state as unknown as MatchState,
        command as Command,
        before.meta.ruleset as unknown as Ruleset,
      );
      optimistic = {
        ...before,
        state: next as unknown as Snapshot["state"],
        phase: phaseLocal(next, before.meta.ruleset as unknown as Ruleset,
                          before.meta.squads ?? {}) as unknown as Phase,
      };
      accept(optimistic, "local");
      setMods(NO_MODS);
      setMsg(null);
    } catch (e) {
      // The engine refused before anything left the device. Say so instantly
      // and do not queue a ball that is simply wrong.
      // A desync means the screen is behind the ledger. Fetching the truth is
      // something the app can do; telling someone at a ground to reload is not.
      if (e instanceof RuleViolation && e.code === "BATTER_DESYNC") {
        setMsg({ text: "Catching up with the match — try that again.", kind: "warn" });
        localAhead.current = 0;
        void load();
        return;
      }
      // A RuleViolation carries its own explanation. Anything else is a bug in
      // this app rather than a problem with the delivery, and should say so
      // rather than blaming the scorer's tap.
      if (e instanceof RuleViolation) {
        setMsg({ text: e.message, kind: "err" });
      } else {
        console.error("local apply threw", e);
        setMsg({ text: "Something went wrong recording that. It has not been saved.", kind: "err" });
      }
      return;
    }

    const clientEventId = await queue(matchId, command);
    void countQueued();

    try {
      const r = await api<{ success: boolean; snapshot?: Snapshot; error?: { code: string; message: string } }>(
        `/api/v1/matches/${matchId}/commands`, { json: { clientEventId, command, leaseToken: lease } });
      if (r.ok && r.data.success) {
        await settle(clientEventId);
        // The server is authoritative. In practice it agrees, because both ran
        // the same reducer over the same ledger.
        if (r.data.snapshot) { localAhead.current = 0; setSnap(r.data.snapshot); }
      } else {
        await settle(clientEventId);
        const e = r.data.error as { code?: string } | undefined;
        if (e?.code === "LEASE_LOST" || e?.code === "NO_LEASE") {
          setLease(null);
          // The panel below already explains this and offers the button. A red
          // banner saying it a third time is noise.
          setMsg(null);
        } else if (e?.code === "BATTER_DESYNC" || e?.code === "BOWLER_DESYNC") {
          // The screen is behind the ledger. Fetching the truth is something
          // the app can do; telling someone at a ground to reload is not.
          localAhead.current = 0;
          setMsg({ text: "Catching up with the match — try that again.", kind: "warn" });
          void load();
        } else {
          setMsg({ text: errorText(r.data, r.status), kind: "err" });
        }
        localAhead.current = 0;
        setSnap(before);          // roll the optimistic apply back
      }
    } catch {
      // Offline. The ball is already on screen and already in the outbox; it
      // will send itself when signal returns.
      await failed(clientEventId, "network");
      setMsg({ text: "Saved on this device — it will send when you are back online.", kind: "warn" });
    } finally {
      void countQueued();
    }
  }, [matchId, lease, countQueued]);

  /**
   * Undo, from wherever the scorer is.
   *
   * The ledger voids the last event whatever kind it was, so this has always
   * been able to take back a wrong batter or bowler — but Undo only existed on
   * the keypad, which is the one screen you are NOT on after picking the wrong
   * player. That is why a mis-tapped batter felt permanent.
   */
  const undoLast = useCallback(async () => {
    const r = await api(`/api/v1/matches/${matchId}/undo`, { json: { leaseToken: lease } });
    if (!r.ok) setMsg({ text: "Nothing to undo.", kind: "warn" });
    void load();
  }, [matchId, lease, load]);

  const drain = useCallback(async () => {
    const r = await syncNow();
    void countQueued();
    if (r.sent > 0) void load();
  }, [countQueued, load]);

  const takeLease = async (force: boolean) => {
    const r = await api<{ ok: boolean; leaseToken?: string; holder?: { displayName: string }; code?: string }>(
      `/api/v1/matches/${matchId}/lease`, { json: { force } });
    if (r.ok && r.data.leaseToken) {
      setLease(r.data.leaseToken); setHeldBy(null);
      void rememberLease(matchId, r.data.leaseToken);
      setMsg({ text: "You have the scoring pen.", kind: "ok" });
      void drain();
    } else if (r.data.holder) {
      setHeldBy(r.data.holder.displayName);
      setMsg({ text: `${r.data.holder.displayName} is scoring this match.`, kind: "warn" });
    } else {
      setMsg({ text: "Could not take the scoring pen.", kind: "err" });
    }
  };

  /* ── music ────────────────────────────────────────────────────────────── */

  const stopTrack = () => {
    music.current?.pause();
    setPlaying(null);
  };

  const toggleTrack = (t: { id: string; title: string }) => {
    if (playing === t.id) {
      music.current?.pause();
      setPlaying(null);
      return;
    }
    // A plain audio element, separate from anything the commentary uses. An
    // anthem is never ducked by a wicket shout, and vice versa.
    if (!music.current) music.current = new window.Audio();
    music.current.volume = mix.get("music");
    music.current.onended = () => setPlaying(null);
    music.current.onerror = () => setPlaying(null);
    setPlaying(t.id);
    // Through the media store rather than a bare src: an <audio> element cannot
    // send an Authorization header, so on Android the request came back 401 and
    // played silence. Fetching the bytes also puts them on the device.
    void mediaUrl(`/api/v1/audio/${t.id}/stream`).then((url) => {
      if (!url || !music.current) { setPlaying(null); return; }
      music.current.src = url;
      void music.current.play().catch(() => setPlaying(null));
    });
  };

  // Riding the music fader mid-anthem is the whole point of having one, so it
  // applies to the element that is already playing rather than the next.
  useEffect(() => mix.onChange((m) => {
    if (music.current) music.current.volume = m.music;
  }), []);

  /* ── building a delivery ──────────────────────────────────────────────── */

  const record = (runs: number, opts: { zone?: string; wicketKind?: string; outId?: string } = {}) => {
    if (!inn || !inn.bowlerId) return;
    // Belt and braces: the keypad should not be on screen in these phases, but
    // a stale render or a fast tap during a phase change must not send a ball
    // the engine is bound to refuse.
    const phase = snap?.phase.kind;
    if (phase === "AWAITING_BOWLER") {
      setMsg({ text: "Choose a bowler before the next ball.", kind: "warn" });
      return;
    }
    if (phase === "AWAITING_BATTER") {
      setMsg({ text: "A batter must come in before the next ball.", kind: "warn" });
      return;
    }

    // Take the striker from the phase the engine published, not by recomputing
    // it here. Two implementations of "who is on strike" is one too many: they
    // agreed almost always, and when they did not the server refused the ball
    // with BATTER_DESYNC and the scorer was told to reload mid-over.
    const ready = snap?.phase.kind === "READY" ? snap.phase : null;
    const striker = ready ? ready.striker : inn.atEnd[inn.bowlerEnd === "END_A" ? "END_B" : "END_A"];
    const nonStriker = ready ? ready.nonStriker : inn.atEnd[inn.bowlerEnd];
    if (!striker || !nonStriker) return;
    const m = mods;
    const isCaught = opts.wicketKind === "CAUGHT";
    const delivery: Record<string, unknown> = {
      bowlerId: inn.bowlerId, strikerId: striker, nonStrikerId: nonStriker,
      wide: m.wide, noBall: m.noBall,
      runsOffBat: m.byes || m.legByes || m.wide || isCaught ? 0 : runs,
      batBoundary: !m.byes && !m.legByes && !m.wide && !isCaught
        ? (runs === 4 ? "FOUR" : runs === 6 ? "SIX" : null) : null,
      byes: m.byes ? runs : 0,
      legByes: m.legByes ? runs : 0,
      runsRun: m.wide && !isCaught ? runs : 0,
      penaltyRuns: 0, penaltyTo: "BATTING", battersCrossed: false,
    };
    if (opts.zone) delivery["bonusZoneId"] = opts.zone;
    if (opts.wicketKind) {
      delivery["wicket"] = { kind: opts.wicketKind, dismissedBatterId: opts.outId ?? striker };
    }
    void send({ type: "RECORD_DELIVERY", delivery });
  };

  /**
   * Speak new lines as they arrive.
   *
   * Keyed on the feed length rather than the text, so a re-render never says
   * the same ball twice. On first load it jumps to the end without reading the
   * whole innings aloud.
   */
  useEffect(() => {
    if (!voiceOn || !snap) return;
    const lines = buildFeed(snap, name, lang);
    if (spokenUpTo.current < 0) { spokenUpTo.current = lines.length; return; }
    const fresh = lines.slice(spokenUpTo.current);
    spokenUpTo.current = lines.length;
    for (const l of fresh) {
      voice.say(l.text, l.kind, lang, { interrupt: l.kind === "result", bank: l.bank ?? null });
    }
    // A ball has been described, so the gap starts again from here.
    gap.current?.onBall();
  }, [snap, voiceOn, lang]);

  /**
   * Keep talking between balls.
   *
   * Everything else the app says is pinned to a delivery, so the sound arrived
   * in bursts with long silences between. This fills the wait with where the
   * game actually is, and a new ball cancels it mid-word — filler is the
   * lowest-priority sound here and must never be heard over a wicket.
   */
  useEffect(() => {
    if (!voiceOn) { gap.current?.stop(); return; }
    const f = new Filler({
      speak: (text) => voice.say(text, "situation", lang, { interrupt: false }),
      situation: () => {
        const snapNow = snapRef.current;
        if (!snapNow) return null;
        const out: FeedOut = {};
        buildFeed(snapNow, (id) => snapNow.meta.players[id] ?? "—", lang, out);
        return out.situation ?? null;
      },
      lang: () => lang,
      level: () => filler.getChatter(),
    });
    gap.current = f;
    f.start();
    const off = filler.onChatterChange(() => f.refresh());
    return () => { off(); f.stop(); gap.current = null; };
  }, [voiceOn, lang]);

  /* ── render ───────────────────────────────────────────────────────────── */

  if (!snap || !rs) {
    return <div className="flex h-full items-center justify-center text-body text-faint">Loading the match…</div>;
  }

  // A brand-new match has no innings yet. The first cut treated that as
  // "still loading" and sat there forever — there was no way to begin.
  if (!inn) {
    return (
      <OpenInnings
        snap={snap} lease={lease} heldBy={heldBy} msg={msg}
        onTakeLease={takeLease} onLeave={onLeave}
        onStart={(battingTeamId, bowlingTeamId, order) =>
          void send({ type: "START_INNINGS", battingTeamId, bowlingTeamId, battingOrder: order })}
      />
    );
  }

  const phase: Phase = !lease ? { kind: "NO_INNINGS" } : snap.phase;
  const se = inn.bowlerEnd === "END_A" ? "END_B" : "END_A";
  const team = snap.meta.teams[inn.battingTeamId];
  const per = rs.ballsPerOver;
  const rr = inn.legalBalls ? inn.totalRuns / (inn.legalBalls / per) : 0;
  const target = snap.state.target;
  const need = target !== null ? target - inn.totalRuns : null;
  const left = rs.oversPerInnings * per - inn.legalBalls;

  return (
    <div className="flex h-full flex-col">
      <header className="flex shrink-0 items-center gap-2.5 border-b border-line px-4 pb-2.5
                         pt-[calc(10px+var(--sat))]">
        <button onClick={onLeave} aria-label="Back"
          className="flex h-11 w-11 items-center justify-center text-faint"><IconBack /></button>
        <span className="font-mono text-meta tracking-[.2em]">SCORING</span>
        <span className="ml-auto flex items-center gap-3">
          {queued > 0 && (
            <span className="font-mono text-meta text-amber">{queued} waiting</span>
          )}
          <span className="flex items-center gap-1.5 font-mono text-meta text-faint">
            <i className={`h-[7px] w-[7px] rounded-full ${
              conn === "live" ? "bg-crease" : conn === "polling" ? "bg-amber" : "bg-leather"}`} />
            {/* "synced" used to mean 'I hold a lease token', which is a fact
                about this device and not about the server. A phone showing
                synced while nothing could actually be sent is exactly how a
                stalled match goes unnoticed — so it now says synced only when
                there is genuinely nothing waiting. */}
            {!online ? "offline"
              : conn !== "live" ? conn
              : !lease ? "watching"
              : queued > 0 ? "sending" : "synced"}
          </span>
        </span>
      </header>

      <div className="flex shrink-0 border-b border-line">
        {([["play","Play"],["card","Scorecard"],["overs","Overs"]] as const).map(([k, label]) => (
          <button key={k} onClick={() => setStatsTab(k)}
            className={`min-h-[44px] flex-1 py-2.5 font-mono text-label uppercase tracking-[.14em]
              ${statsTab === k ? "border-b-2 border-amber text-amber" : "text-faint"}`}>
            {label}
          </button>
        ))}
      </div>

      <main className="min-h-0 flex-1 overflow-y-auto">
        {statsTab === "card" ? (
          <div className="mx-auto w-full max-w-[760px]"><Scorecard snap={snap} name={name} /></div>
        ) : statsTab === "overs" ? (
          <div className="mx-auto w-full max-w-[760px]"><OverByOver snap={snap} name={name} /></div>
        ) : (
        <div className="mx-auto w-full max-w-[760px] px-4 split:px-6">
          <div className="pt-3.5 font-mono text-meta uppercase tracking-[.13em] text-faint">
            {team?.name} · {rs.name}
          </div>
          <div className="flex items-baseline gap-3.5">
            <div className="font-num text-mega font-semibold leading-none tracking-tight">
              {inn.totalRuns}
              {/* In double wicket nobody is out, so a wickets column would read
                  /0 all innings. Show the dismissals that cost runs instead. */}
              <span className="text-[#8B95A1]">
                /{doubleWicket ? dismissals(inn) : inn.wickets}
              </span>
            </div>
            <div className="ml-auto text-right">
              <b className="block font-num text-big font-semibold leading-none">
                {Math.floor(inn.legalBalls / per)}.{inn.legalBalls % per}
              </b>
              <span className="font-mono text-label tracking-[.13em] text-faint">
                OF {rs.oversPerInnings} OV
              </span>
            </div>
          </div>
          <div className="pt-1.5 font-mono text-caption text-faint">RR {rr.toFixed(2)}</div>

          {inn.penaltyRuns !== 0 && (
            <div className="pt-1.5 font-mono text-caption text-leather">
              {inn.penaltyRuns} from {dismissals(inn)} wicket{dismissals(inn) === 1 ? "" : "s"}
            </div>
          )}
          {need !== null && need > 0 && left > 0 && (
            <div className="pt-2 font-mono text-body font-semibold text-amber">
              Need {need} from {left} ball{left === 1 ? "" : "s"} · {(need / (left / per)).toFixed(2)}/ov
            </div>
          )}

          {/* batters */}
          <div className="mt-3.5 overflow-hidden rounded-xl border border-line bg-panel">
            {(["END_A", "END_B"] as const).map((end) => {
              const pid = inn.atEnd[end];
              if (!pid) return null;
              const b = inn.batters[pid];
              return (
                <div key={end} className="flex items-center gap-3 border-b border-line px-4 py-3 last:border-b-0">
                  <span className="w-2.5 text-label text-amber">{end === se ? "▸" : ""}</span>
                  <span className="min-w-0 flex-1 truncate">{name(pid)}</span>
                  <span className="font-mono text-lead font-semibold">
                    {b?.runs ?? 0} <span className="font-normal text-faint">({b?.ballsFaced ?? 0})</span>
                  </span>
                  <span className="hidden min-w-[70px] text-right font-mono text-meta text-faint split:block">
                    4×{b?.fours ?? 0} 6×{b?.sixes ?? 0}
                  </span>
                </div>
              );
            })}
          </div>

          {inn.bowlerId && (
            <div className="mt-2.5 overflow-hidden rounded-xl border border-line bg-panel">
              <div className="flex items-center gap-3 px-4 py-3">
                <span className="min-w-0 flex-1 truncate">{name(inn.bowlerId)}</span>
                <span className="font-mono text-lead font-semibold">
                  {Math.floor((inn.bowlers[inn.bowlerId]?.legalBalls ?? 0) / per)}.
                  {(inn.bowlers[inn.bowlerId]?.legalBalls ?? 0) % per}–
                  {inn.bowlers[inn.bowlerId]?.maidens ?? 0}–
                  {inn.bowlers[inn.bowlerId]?.runsConceded ?? 0}–
                  {inn.bowlers[inn.bowlerId]?.wickets ?? 0}
                </span>
              </div>
            </div>
          )}

          <div className="mt-3.5">
            <VoiceBar lang={lang} setLang={setLang} voiceOn={voiceOn}
                      setVoiceOn={(v) => { spokenUpTo.current = -1; setVoiceOn(v); }} />
            <Feed snap={snap} name={name} lang={lang} limit={14} voice={saidAloud} />
          </div>

          {persistWarn && (
            <p className="mt-3 font-mono text-meta leading-relaxed text-amber">
              This browser would not promise to keep offline data. Export mid-match if signal is poor.
            </p>
          )}
        </div>
        )}
      </main>

      {trackSheet && (
        <TrackSheet
          tracks={tracks} playing={playing}
          onPick={(t) => { toggleTrack(t); setTrackSheet(false); }}
          onStop={() => { stopTrack(); setTrackSheet(false); }}
          onClose={() => setTrackSheet(false)}
        />
      )}

      {/* ── the control zone. Built per phase, never merely hidden. ──────── */}
      {/* The keypad only exists when the engine says a ball can be bowled.
          Rendering it in AWAITING_BOWLER let a tap through that the engine then
          refused — an error the scorer could have been spared entirely. */}
      {statsTab === "play" && (
      <div className="shrink-0 border-t border-line px-3 pb-[calc(12px+var(--sab))] pt-3">
        <div className="mx-auto w-full max-w-[760px]">
          {msg && <Msg text={msg.text} kind={msg.kind} />}

          {/* Music is one icon, not a row of pills. Twenty-five tracks scrolling
              sideways above a keypad is a swipe hunt mid-over; a sheet is a tap. */}
          {lease && !wicket && (
            <div className="mb-2 flex items-center gap-2">
              <TalkButton matchId={matchId} inn={inn} per={per}
                          sendSignal={sendSignal} rtcIn={rtcIn} />

              {tracks.length > 0 && (
                <button onClick={() => setTrackSheet(true)}
                  aria-label="Match audio"
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border
                    ${playing ? "border-amber bg-amber text-board" : "border-line text-faint"}`}>
                  <IconMusic size={19} />
                </button>
              )}

              {playing && (
                <>
                  <span className="min-w-0 flex-1 truncate font-mono text-caption text-amber">
                    {tracks.find((t) => t.id === playing)?.title}
                  </span>
                  <button onClick={stopTrack}
                    className="flex h-11 shrink-0 items-center rounded-full border border-line px-3
                               font-mono text-meta text-faint">
                    STOP
                  </button>
                </>
              )}
            </div>
          )}

          {wicket ? (
            <WicketPicker
              mods={mods} inn={inn} name={name}
              striker={wicket.striker} nonStriker={wicket.nonStriker}
              onPick={(kind, outId) => { setWicket(null); record(0, { wicketKind: kind, outId }); }}
              onCancel={() => setWicket(null)}
            />
          ) : !lease ? (
            <>
              {/* One statement, not three. This showed a red error, a grey
                  restatement of it, and a button saying the same thing again. */}
              <Note>
                {heldBy
                  ? <><b className="text-amber">{heldBy}</b> is scoring this match.</>
                  : "Take the pen to start scoring."}
              </Note>
              <Button full onClick={() => {
                if (heldBy && !confirm(`${heldBy} is scoring. Take over anyway?`)) return;
                void takeLease(!!heldBy);
              }}>{heldBy ? `Take over from ${heldBy}` : "Take the scoring pen"}</Button>
            </>
          ) : phase.kind === "INNINGS_BREAK" ? (
            // This used to fall into the COMPLETE branch, which offered "Done"
            // and no way to begin the second innings.
            <>
              <Note>
                Innings complete. <b className="text-amber">
                  {snap.meta.teams[phase.battingTeamId]?.name}
                </b> bat next, chasing {(snap.state.innings[0]?.totalRuns ?? 0) + 1}.
              </Note>
              <Pick
                items={(snap.meta.squads?.[phase.battingTeamId] ?? [])
                  .map((p2) => ({ id: p2, label: name(p2) }))}
                onPick={(p2) => {
                  const order = snap.meta.squads?.[phase.battingTeamId] ?? [];
                  // Put whoever was tapped on strike, then the rest of the side.
                  const rest = order.filter((x) => x !== p2);
                  void send({ type: "START_INNINGS",
                    battingTeamId: phase.battingTeamId,
                    bowlingTeamId: phase.bowlingTeamId,
                    battingOrder: [p2, ...rest] });
                }} />
            </>
          ) : phase.kind === "COMPLETE" || (inn.complete && snap.state.complete) ? (
            <>
              <Note>{snap.state.result?.summary ?? "This match is complete."}</Note>
              <Button full kind="ghost" onClick={onLeave}>Done</Button>
            </>
          ) : phase.kind === "AWAITING_BATTER" ? (
            <>
              <Note>A wicket has fallen. Choose the <b className="text-amber">next batter</b>.</Note>
              <Pick items={phase.available.map((p) => ({ id: p, label: name(p) }))}
                    onPick={(p) => void send({ type: "SELECT_BATTER", playerId: p })} />
              {/* The wicket itself was the last event, so this takes it back —
                  which is also how you undo a batter you have just mis-picked. */}
              {(snap.canUndo ?? false) && (
                <Button full kind="ghost" onClick={() => void undoLast()}>Undo the last ball</Button>
              )}
            </>
          ) : phase.kind === "AWAITING_BOWLER" ? (
            <>
              <Note>
                {inn.legalBalls === 0 && inn.overIndex === 0
                  ? <>Choose who <b className="text-amber">opens the bowling</b>.</>
                  : <>Over complete. Choose the <b className="text-amber">next bowler</b>.</>}
              </Note>
              <Pick
                items={[...phase.available.map((p) => ({ id: p, label: name(p) })),
                        ...phase.blocked.map((p) => ({ id: p, label: name(p), sub: "unavailable", disabled: true }))]}
                onPick={(p) => void send({ type: "SELECT_BOWLER", playerId: p })} />
              {(snap.canUndo ?? false) && (
                <Button full kind="ghost" onClick={() => void undoLast()}>Undo the last ball</Button>
              )}
            </>
          ) : (
            <>
            <Keypad
              mods={mods} setMods={setMods} busy={false} freeHit={inn.freeHit}
              zones={rs.bonusZones}
              onRuns={(n, zone) => record(n, zone ? { zone } : {})}
              onWicket={() => {
                const s = inn.atEnd[se], n = inn.atEnd[inn.bowlerEnd];
                if (s && n) setWicket({ striker: s, nonStriker: n });
              }}
              canUndo={snap.canUndo ?? false}
              canRedo={snap.canRedo ?? false}
              onUndo={() => void api(`/api/v1/matches/${matchId}/undo`, { json: { leaseToken: lease } })
                .then((r) => { if (!r.ok) setMsg({ text: "Nothing to undo.", kind: "warn" }); void load(); })}
              onRedo={() => void api<{ error?: { message: string } }>(
                `/api/v1/matches/${matchId}/redo`, { json: { leaseToken: lease } })
                .then((r) => {
                  if (!r.ok) setMsg({ text: r.data.error?.message ?? "Nothing to redo.", kind: "warn" });
                  void load();
                })}
            />
            </>
          )}

          {/**
           * Ending an innings is not a mid-over decision.
           *
           * This used to render only in the READY phase — that is, only between
           * balls with both batters at the crease. But the moment a scorer
           * actually needs it is when the innings cannot continue: nobody left
           * to bat, nobody left to bowl, rain, or everyone has to go home. Those
           * are exactly the AWAITING_BATTER and AWAITING_BOWLER screens, where
           * it was not there.
           *
           * So it sits outside the phase branches now, below whatever the
           * scorer is being asked for, available whenever a match is in
           * progress.
           */}
          {phase.kind !== "COMPLETE" && (
            <MatchControls
              onUndo={undoLast}
              canUndo={snap.canUndo ?? false}
              onEndInnings={() => void send({ type: "END_INNINGS", reason: "DECLARED" })}
              lastInnings={snap.state.innings.length >= 2}
              stuck={phase.kind === "AWAITING_BATTER" && phase.available.length === 0}
            />
          )}
        </div>
      </div>
      )}
    </div>
  );
}

function OpenInnings({ snap, lease, heldBy, msg, onTakeLease, onStart, onLeave }: {
  snap: Snapshot;
  lease: string | null;
  heldBy: string | null;
  msg: { text: string; kind: "err" | "ok" | "warn" } | null;
  onTakeLease: (force: boolean) => void;
  onStart: (batting: string, bowling: string, order: string[]) => void;
  onLeave: () => void;
}) {
  const batting = snap.meta.battingFirstTeamId;
  const bowling = Object.keys(snap.meta.teams).find((t) => t !== batting) ?? "";
  const squad = snap.meta.squads?.[batting] ?? [];
  const [order, setOrder] = useState<string[]>([]);
  const name = (id: string) => snap.meta.players[id] ?? "—";

  const toggle = (pid: string) => {
    setOrder((o) => (o.includes(pid) ? o.filter((x) => x !== pid) : [...o, pid]));
  };

  return (
    <div className="flex h-full flex-col">
      <header className="flex shrink-0 items-center gap-2.5 border-b border-line px-4 pb-2.5
                         pt-[calc(10px+var(--sat))]">
        <button onClick={onLeave} aria-label="Back"
          className="flex h-11 w-11 items-center justify-center text-faint"><IconBack /></button>
        <span className="font-mono text-meta tracking-[.2em]">NEW MATCH</span>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-[760px] px-4 py-5 split:px-6">
          {msg && <Msg text={msg.text} kind={msg.kind} />}

          <h1 className="font-num text-head font-semibold">
            {snap.meta.teams[batting]?.name} bat first
          </h1>
          <p className="mt-1 text-body text-faint">
            Tap players in batting order. The first two go to the crease.
          </p>

          <div className="mt-4 overflow-hidden rounded-xl border border-line bg-panel">
            {squad.map((pid) => {
              const i = order.indexOf(pid);
              return (
                <button key={pid} onClick={() => toggle(pid)}
                  className="flex w-full items-center gap-3 border-b border-line px-4 py-3 text-left last:border-b-0">
                  <span className={`w-6 font-mono text-body font-semibold
                    ${i >= 0 ? "text-amber" : "text-faint"}`}>
                    {i >= 0 ? i + 1 : "·"}
                  </span>
                  <span className="flex-1">{name(pid)}</span>
                  {i === 0 && <span className="font-mono text-label text-faint">STRIKER</span>}
                  {i === 1 && <span className="font-mono text-label text-faint">NON-STRIKER</span>}
                </button>
              );
            })}
            {squad.length === 0 && (
              <p className="px-4 py-6 text-center text-body text-faint">
                That team has nobody in its squad.
              </p>
            )}
          </div>
        </div>
      </main>

      <div className="shrink-0 border-t border-line px-3 pb-[calc(12px+var(--sab))] pt-3">
        <div className="mx-auto w-full max-w-[760px]">
          {!lease ? (
            <>
              <p className="pb-2.5 text-center font-mono text-caption text-faint">
                {heldBy ? `${heldBy} holds the scoring pen.` : "Take the pen to start."}
              </p>
              <Button full onClick={() => onTakeLease(!!heldBy)}>
                {heldBy ? `Take over from ${heldBy}` : "Take the scoring pen"}
              </Button>
            </>
          ) : (
            <Button full disabled={order.length < 2}
              onClick={() => onStart(batting, bowling, order)}>
              {order.length < 2 ? "Pick at least two batters" : `Start with ${name(order[0]!)} on strike`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}



/** The audio picker. A sheet, so a long library is a scroll rather than a swipe. */
function TrackSheet({ tracks, playing, onPick, onStop, onClose }: {
  tracks: { id: string; title: string }[];
  playing: string | null;
  onPick: (t: { id: string; title: string }) => void;
  onStop: () => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState("");
  const shown = q.trim()
    ? tracks.filter((t) => t.title.toLowerCase().includes(q.trim().toLowerCase()))
    : tracks;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 split:items-center split:p-6"
         onClick={onClose}>
      <div className="flex max-h-[80vh] w-full max-w-[520px] flex-col rounded-t-2xl border border-line
                      bg-panel pb-[calc(14px+var(--sab))] split:rounded-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 border-b border-line px-4 py-3.5">
          <IconMusic size={19} />
          <b className="flex-1 font-num text-sub font-semibold">Match audio</b>
          <span className="font-mono text-meta text-faint">{tracks.length}</span>
        </div>

        {tracks.length > 8 && (
          <div className="px-4 pt-3">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
              className="w-full rounded-card border border-line bg-[#0F1317] px-3 py-2.5
                         text-body text-chalk outline-none placeholder:text-faint focus:border-amber" />
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
          {shown.length === 0 ? (
            <p className="px-2 py-8 text-center text-body text-faint">Nothing matches that.</p>
          ) : shown.map((t) => (
            <button key={t.id} onClick={() => onPick(t)}
              className={`flex w-full items-center gap-3 rounded-card px-3 py-3 text-left
                ${playing === t.id ? "bg-[#20180C] text-amber" : "hover:bg-raise"}`}>
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border
                               border-line font-mono text-meta">
                {playing === t.id ? "■" : "▶"}
              </span>
              <span className="min-w-0 flex-1 truncate text-body font-semibold">{t.title}</span>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-2 border-t border-line px-3 pt-3">
          <button onClick={onStop} disabled={!playing}
            className="rounded-card border border-line py-3 font-mono text-meta uppercase
                       tracking-[.13em] text-faint disabled:opacity-35">
            Stop
          </button>
          <button onClick={onClose}
            className="rounded-card border border-line py-3 font-mono text-meta uppercase
                       tracking-[.13em] text-faint">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/** Dismissals that cost runs rather than ending an innings. */
function dismissals(inn: Innings): number {
  return Object.values(inn.batters).reduce((a, b) => a + (b.dismissals ?? 0), 0);
}

/**
 * Push to talk.
 *
 * Held, not toggled. A scorer is standing among people who are also talking,
 * and an open mic would broadcast all of it; holding a button is also the only
 * gesture that reliably means "I meant to say that".
 *
 * Every press does two things at once, and the difference between them matters:
 *
 *   Live      Anyone watching who tapped Listen hears it as it is said, over
 *             WebRTC, peer to peer. Bounded by how many listeners the scorer's
 *             uplink can carry, and it does not survive a bad network.
 *   Recorded  The same press is recorded and uploaded, exactly as before, and
 *             lands in the feed for everyone including whoever was not watching
 *             at the time. This is the record. It is never skipped, and if the
 *             live path fails nothing is lost but the few seconds.
 *
 * Recording and broadcasting share one microphone track — a second
 * getUserMedia would mean a second echo canceller fighting the first.
 *
 * No permission is requested until the first press, so a scorer who never uses
 * this is never asked.
 */
function TalkButton({ matchId, inn, per, sendSignal, rtcIn }: {
  matchId: string; inn: Innings | null; per: number;
  sendSignal: (m: Record<string, unknown>) => void;
  rtcIn: React.MutableRefObject<((m: Record<string, unknown>) => void) | null>;
}) {
  const [state, setState] = useState<mic.MicState>("idle");
  const [note, setNote] = useState<string | null>(null);
  const [seconds, setSeconds] = useState(0);
  const [heard, setHeard] = useState(0);
  const tick = useRef<ReturnType<typeof setInterval> | null>(null);
  const caster = useRef<rtc.Broadcaster | null>(null);
  /**
   * One stream to a server, rather than one per listener.
   *
   * Preferred when it is configured. The mesh stays as the fallback so a
   * misconfigured or unreachable SFU costs latency, not commentary.
   */
  const sfu = useRef<Publisher | null>(null);

  useEffect(() => mic.onMic((s, detail) => {
    setState(s);
    if (detail) setNote(detail);
  }), []);

  // The broadcaster exists for as long as the keypad does, so listeners can
  // connect between presses rather than during one.
  useEffect(() => {
    if (!rtc.supported()) return;
    let cancelled = false;
    void sfuAvailable().then((ok) => { if (ok && !cancelled) sfu.current = new Publisher(); });
    const b = new rtc.Broadcaster(sendSignal, (st) => setHeard(st.connected));
    caster.current = b;
    rtcIn.current = (m) => { void b.handle(m); };
    return () => {
      cancelled = true;
      rtcIn.current = null;
      caster.current = null;
      sfu.current?.close();
      sfu.current = null;
      sendSignal({ type: "RTC_UNPUBLISH" });
      b.close();
    };
  }, [sendSignal, rtcIn]);

  useEffect(() => () => mic.release(), []);

  if (!mic.supported()) return null;

  const begin = async () => {
    setNote(null);
    // Enable the outgoing track BEFORE the recorder starts, or the first
    // fraction of a second is recorded as silence.
    const stream = await mic.acquire();
    if (stream && sfu.current) {
      // Publish once, then announce. Announcing before the track is up would
      // send listeners after a stream that is not there yet.
      if (!sfu.current.publication) {
        const pub = await sfu.current.publish(stream);
        if (pub) sendSignal({ type: "RTC_PUBLISH", ...pub });
      }
      sfu.current.setTalking(true);
    } else if (stream && caster.current) {
      await caster.current.init(stream);
      caster.current.setTalking(true);
    }
    const ok = await mic.start();
    if (!ok) { caster.current?.setTalking(false); return; }
    setSeconds(0);
    tick.current = setInterval(() => setSeconds((v) => v + 0.1), 100);
  };

  const end = async () => {
    if (tick.current) { clearInterval(tick.current); tick.current = null; }
    const clip = await mic.stop();
    sfu.current?.setTalking(false);
    caster.current?.setTalking(false);
    setSeconds(0);
    if (!clip) return;
    setState("sending");
    const at = {
      over: inn ? Math.floor(inn.legalBalls / per) : 0,
      ball: inn ? inn.legalBalls % per : 0,
    };
    const r = await mic.upload(matchId, clip, at);
    setState("idle");
    setNote(r.ok ? null : r.error ?? "Could not send that.");
  };

  const recording = state === "recording";
  return (
    <>
      <button
        onPointerDown={(e) => { e.preventDefault(); void begin(); }}
        onPointerUp={(e) => { e.preventDefault(); void end(); }}
        onPointerLeave={() => { if (recording) void end(); }}
        onContextMenu={(e) => e.preventDefault()}
        aria-label={recording ? "Release to send" : "Hold to commentate"}
        className={`flex h-11 shrink-0 items-center gap-1.5 rounded-full border px-3
                    ${recording ? "border-leather bg-leather text-chalk"
                      : state === "sending" ? "border-amber text-amber"
                      : "border-line text-faint"}`}>
        <IconMic size={17} />
        <span className="font-mono text-label uppercase tracking-[.1em]">
          {recording ? seconds.toFixed(1) : state === "sending" ? "…" : "Talk"}
        </span>
      </button>

      {/* How many people are actually hearing this as it is said. Worth
          showing: it is the difference between talking to a ground and
          talking to a phone. */}
      {heard > 0 && (
        <span className="flex shrink-0 items-center gap-1 font-mono text-label
                         uppercase tracking-[.1em] text-crease">
          <i className="h-[7px] w-[7px] rounded-full bg-crease" />
          {heard} live
        </span>
      )}

      {note && (
        <span className="min-w-0 flex-1 truncate font-mono text-meta text-leather">{note}</span>
      )}
    </>
  );
}

/* ── pieces ─────────────────────────────────────────────────────────────── */

function Note({ children }: { children: React.ReactNode }) {
  return <p className="pb-2.5 pt-0.5 text-center font-mono text-caption leading-relaxed text-faint">{children}</p>;
}

function Pick({ items, onPick }: {
  items: { id: string; label: string; sub?: string; disabled?: boolean }[];
  onPick: (id: string) => void;
}) {
  return (
    <div className="grid max-h-[42vh] grid-cols-2 gap-2 overflow-y-auto">
      {items.map((it) => (
        <button key={it.id} disabled={it.disabled} onClick={() => onPick(it.id)}
          className="rounded-card border border-line bg-raise px-3 py-3 text-left text-body
                     font-semibold disabled:opacity-35">
          {it.label}
          {it.sub && <span className="mt-0.5 block font-mono text-meta font-normal text-faint">{it.sub}</span>}
        </button>
      ))}
      {items.length === 0 && (
        <button disabled className="col-span-2 rounded-card border border-line px-3 py-3 text-faint">
          Nobody available
        </button>
      )}
    </div>
  );
}

function Keypad({ mods, setMods, busy, freeHit, zones, onRuns, onWicket,
                 onUndo, onRedo, canUndo, canRedo }: {
  mods: Mods; setMods: (m: Mods) => void; busy: boolean; freeHit: boolean;
  zones: { id: string; label: string; bonus: number }[];
  onRuns: (n: number, zone?: string) => void; onWicket: () => void;
  onUndo: () => void; onRedo: () => void; canUndo: boolean; canRedo: boolean;
}) {
  const toggle = (k: keyof Mods) => {
    const next = { ...mods };
    if (k === "wide" && !next.wide) next.noBall = false;
    if (k === "noBall" && !next.noBall) next.wide = false;
    if (k === "byes" && !next.byes) next.legByes = false;
    if (k === "legByes" && !next.legByes) next.byes = false;
    next[k] = !next[k];
    setMods(next);
  };
  const mod = (label: string, k: keyof Mods) => (
    <button onClick={() => toggle(k)}
      className={`rounded-card border px-0 py-3 font-mono text-caption font-semibold tracking-wider
        ${mods[k] ? "border-amber bg-[#20180C] text-amber" : "border-line bg-raise text-faint"}`}>
      {label}
    </button>
  );
  const key = (n: number, tone?: "a" | "w") => (
    <button disabled={busy} onClick={() => onRuns(n)}
      className={`rounded-card border bg-raise py-4 text-title font-semibold disabled:opacity-30
        ${tone === "a" ? "border-[#4A3A1C] text-amber" : tone === "w" ? "border-[#3A2422] text-leather" : "border-line text-chalk"}`}>
      {n}
    </button>
  );

  return (
    <>
      {freeHit && <Note><b className="text-amber">FREE HIT</b></Note>}
      <div className="mb-2 grid grid-cols-5 gap-2">
        {mod("WD", "wide")}{mod("NB", "noBall")}{mod("B", "byes")}{mod("LB", "legByes")}
        <button onClick={() => setMods(NO_MODS)}
          className="rounded-card border border-line bg-raise py-3 font-mono text-caption text-faint">
          CLR
        </button>
      </div>
      <div className="grid grid-cols-4 gap-2">{key(0)}{key(1)}{key(2)}{key(3)}</div>
      <div className="mt-2 grid grid-cols-4 gap-2">
        {key(4, "a")}{key(6, "a")}
        <button disabled={busy} onClick={onWicket}
          className="rounded-card border border-[#3A2422] bg-raise py-4 text-title font-semibold
                     text-leather disabled:opacity-30">W</button>
        {key(5)}
      </div>

      {zones.length > 0 && (
        <div className="mt-2 flex gap-2 overflow-x-auto">
          {zones.map((z) => (
            <button key={z.id} disabled={busy} onClick={() => onRuns(0, z.id)}
              className="shrink-0 rounded-full border border-crease px-3.5 py-2 font-mono text-caption
                         font-semibold text-crease disabled:opacity-30">
              {z.label} +{z.bonus}
            </button>
          ))}
        </div>
      )}

      <div className="mt-2 grid grid-cols-2 gap-2">
        <button onClick={onUndo} disabled={!canUndo}
          className="flex items-center justify-center gap-1.5 rounded-card border border-line py-3
                     font-mono text-meta uppercase tracking-[.13em] text-faint disabled:opacity-30">
          <IconUndo size={14} /> Undo
        </button>
        <button onClick={onRedo} disabled={!canRedo}
          className="flex items-center justify-center gap-1.5 rounded-card border border-line py-3
                     font-mono text-meta uppercase tracking-[.13em] text-faint disabled:opacity-30">
          <IconRedo size={14} /> Redo
        </button>
      </div>
    </>
  );
}


function WicketPicker({ mods, inn, name, striker, nonStriker, onPick, onCancel }: {
  mods: Mods; inn: Innings; name: (id: string) => string;
  striker: string; nonStriker: string;
  onPick: (kind: string, outId: string) => void; onCancel: () => void;
}) {
  const [kind, setKind] = useState<string | null>(null);
  // Only offer what the engine will accept, so a scorer never taps something
  // that bounces back as an error mid-over.
  const allowed = mods.noBall || inn.freeHit ? DEAD_OK : mods.wide ? WIDE_OK : KINDS.map((k) => k[0]);
  const why = mods.noBall ? "no-ball" : inn.freeHit ? "free hit" : mods.wide ? "wide" : null;

  if (kind === "RUN_OUT") {
    return (
      <>
        <Note>Run out — <b className="text-amber">which batter</b>?</Note>
        <Pick items={[{ id: striker, label: name(striker), sub: "striker" },
                      { id: nonStriker, label: name(nonStriker), sub: "non-striker" }]}
              onPick={(id) => onPick("RUN_OUT", id)} />
        <div className="mt-2"><Button full kind="ghost" onClick={() => setKind(null)}>Back</Button></div>
      </>
    );
  }

  return (
    <>
      <Note>How was the batter out?{why && <> <b className="text-amber">Off a {why}</b></>}</Note>
      <Pick items={KINDS.map(([k, label]) => ({
        id: k, label,
        ...(allowed.includes(k) ? {} : { sub: "not legal here", disabled: true }),
      }))} onPick={(k) => (k === "RUN_OUT" ? setKind("RUN_OUT") : onPick(k, striker))} />
      <div className="mt-2"><Button full kind="ghost" onClick={onCancel}>Cancel</Button></div>
    </>
  );
}


/**
 * The things a scorer needs that are not a ball.
 *
 * Kept behind a tap rather than on the keypad. Ending an innings early is a
 * real and ordinary thing — a side declares, rain comes, everyone has to go
 * home — but it is not something to sit one thumb-width from the run buttons.
 */
function MatchControls({ onUndo, canUndo, onEndInnings, lastInnings, stuck }: {
  onUndo: () => Promise<void>;
  canUndo: boolean;
  onEndInnings: () => void;
  lastInnings: boolean;
  /** Nobody is left to send in — the innings cannot continue. */
  stuck?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // When there is nobody left to bat, hiding the way out behind "More" is
  // exactly wrong: that is the one moment the scorer is looking for it.
  useEffect(() => { if (stuck) setOpen(true); }, [stuck]);

  if (!open) {
    return (
      // "More" told nobody what was behind it, and a scorer looking for the way
      // to end an innings should not have to guess which word hides it.
      <button onClick={() => setOpen(true)}
        className="mt-2 min-h-[44px] w-full font-mono text-label uppercase tracking-[.14em] text-faint">
        Undo · End innings
      </button>
    );
  }

  return (
    <div className="mt-2 rounded-card border border-line bg-panel p-3">
      <div className="flex items-center">
        <SectionLabel>Match</SectionLabel>
        <button onClick={() => { setOpen(false); setConfirming(false); }}
          className="ml-auto min-h-[44px] px-2 font-mono text-label uppercase text-faint">Close</button>
      </div>

      {stuck && (
        <p className="mt-2 text-caption leading-relaxed text-amber">
          There is nobody left to come in. End the innings to move on.
        </p>
      )}

      <div className="mt-1 space-y-2">
        <button onClick={() => void onUndo()} disabled={!canUndo}
          className="min-h-[44px] w-full rounded-control border border-line text-body disabled:opacity-40">
          Undo the last ball
        </button>
        <p className="text-meta leading-relaxed text-faint">
          Takes back whatever was recorded last — a ball, or a batter or bowler chosen
          by mistake. Press it again to keep going further back.
        </p>

        {!confirming ? (
          <button onClick={() => setConfirming(true)}
            className="min-h-[44px] w-full rounded-control border border-[#3A2422] text-body text-leather">
            {lastInnings ? "End the match" : "End this innings"}
          </button>
        ) : (
          <div className="rounded-control border border-line bg-raise p-3">
            <p className="mb-2.5 text-caption leading-relaxed">
              {lastInnings
                ? "End the match here? The result is worked out from the score as it stands."
                : "End this innings early? The other side comes in to chase what is on the board."}
            </p>
            <div className="flex gap-2">
              <button onClick={() => setConfirming(false)}
                className="min-h-[44px] flex-1 rounded-control border border-line text-body">
                Keep playing
              </button>
              <button onClick={() => { setConfirming(false); setOpen(false); onEndInnings(); }}
                className="min-h-[44px] flex-1 rounded-control border border-[#3A2422] text-body text-leather">
                {lastInnings ? "End match" : "End innings"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
